"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import { RoundResult, TeamSnapshot } from "@/lib/types/common/GameState";
import { useMainMap } from "@/app/providers/MainMapContextProvider";
import Image from "next/image";
import { convertDistanceToYalms } from "@/lib/utils/coordinateConversion";
import { useGame } from "@/app/providers/GameContextProvider";
import { useTranslation } from "@/hooks/useTranslation";
import TooltipWithIconFFXIV from "@/app/components/TooltipWithIconFFXIV";
import { PRECISION_MIN_DISTANCE } from "@/lib/types/common/ScoringConstants";
import { Team } from "@/lib/types/common/Team";
import BarFFXIV from "@/app/components/BarFFXIV";
import { motion, AnimatePresence, animate } from "framer-motion";
import { getTeamColorHex } from "@/lib/config/teams";
import PlayerAvatar, { calculateDuplicateNumbers } from "@/app/components/PlayerAvatar";
import { useIsMobile } from "@/hooks/useIsMobile";
import { AUDIO, useAudio } from "@/app/providers/AudioProvider";

// Animation timing constants (in seconds)
const ANIMATION_TIMING = {
    ATTACK_DELAY: 1,           // Delay before attack animation starts
    HP_DECREASE_DURATION: 1,   // Duration of HP bar going down
    HIT_EFFECT_DURATION: 0.3,    // Duration of shake/flash effect
    HIT_FLASH_LINGER: 0.2,       // How long the hit state lingers after animation
};

interface FightModePlayerResultsListProps {
    roundResult: RoundResult;
    onHoverMapPlayer?: (playerId: string | null) => void;
    onClickMapPlayer?: (playerId: string | null) => void;
    lockedMapPlayer?: string | null;
}

interface PlayerResultData {
    sessionId: string;
    name: string;
    guessedOnCorrectMap: boolean;
    distance?: number;
    guessedMapName?: string;
    guessedMapId?: string;
    roundScore: number;
    totalScore: number;
    hasGuessed: boolean;
    avatarId: string;
    playerNumber: number;
    isEliminated: boolean;
    teamId: string;
}

interface TeamData {
    team: Team;
    players: PlayerResultData[];
    roundTotalScore: number;
    snapshot?: TeamSnapshot; // Team state snapshot for this round (from roundResult)
}

// Calculate distance between two points
const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
};

export default function FightModePlayerResultsList({
    roundResult,
    onHoverMapPlayer,
    onClickMapPlayer,
    lockedMapPlayer,
}: FightModePlayerResultsListProps) {
    const { gameState, room } = useGame();
    const { allMaps } = useMainMap();
    const { locale } = useTranslation();
    const isMobile = useIsMobile();

    // Animation state
    const [animatedHp, setAnimatedHp] = useState<Record<string, number>>({});
    const [hitTeams, setHitTeams] = useState<Set<string>>(new Set());
    const hasAnimatedRef = useRef(false);

    // Process player results and organize by team
    const teamData: (TeamData | null)[] = useMemo(() => {
        if (!gameState) return [null, null, null, null];

        const playerResults: PlayerResultData[] = Array.from(roundResult.playerResults.entries()).map(([sessionId, player]) => {
            const guess = player.guesses.get(roundResult.roundNumber.toString());
            const roundScore = player.scores[roundResult.roundNumber - 1] || 0;

            if (!guess) {
                return {
                    sessionId,
                    name: player.name,
                    guessedOnCorrectMap: false,
                    roundScore: 0,
                    totalScore: player.totalScore,
                    hasGuessed: false,
                    avatarId: player.avatarId,
                    playerNumber: player.playerNumber || 0,
                    isEliminated: player.isEliminated || false,
                    teamId: gameState.players?.get(sessionId)?.teamId || '',
                };
            }

            const guessedOnCorrectMap = guess.mapId === roundResult.photosphere.mapId;
            let distance: number | undefined;
            let guessedMapName: string | undefined;

            if (guessedOnCorrectMap) {
                distance = calculateDistance(
                    guess.x,
                    guess.y,
                    roundResult.photosphere.coord.x,
                    roundResult.photosphere.coord.y
                ) - PRECISION_MIN_DISTANCE;
            } else {
                const guessedMap = allMaps.find(map => map.id === guess.mapId);
                guessedMapName = guessedMap ? guessedMap.name[locale as keyof typeof guessedMap.name] : undefined;
            }

            return {
                sessionId,
                name: player.name,
                guessedOnCorrectMap,
                distance,
                guessedMapName,
                guessedMapId: guess.mapId,
                roundScore,
                totalScore: player.totalScore,
                hasGuessed: true,
                avatarId: player.avatarId,
                playerNumber: player.playerNumber || 0,
                isEliminated: player.isEliminated || false,
                teamId: gameState.players?.get(sessionId)?.teamId || '',
            };
        });

        // Organize by team - always create 4 slots
        const teamsMap = new Map<string, TeamData>();

        // Create a map of team snapshots for easy lookup
        const snapshotMap = new Map<string, TeamSnapshot>();
        if (roundResult.teamSnapshots) {
            for (const snapshot of Array.from(roundResult.teamSnapshots)) {
                snapshotMap.set(snapshot.teamId, snapshot);
            }
        }

        for (const team of Array.from(gameState.teams)) {
            teamsMap.set(team.id, {
                team,
                players: [],
                roundTotalScore: 0,
                snapshot: snapshotMap.get(team.id),
            });
        }

        for (const player of playerResults) {
            const teamData = teamsMap.get(player.teamId);
            if (teamData) {
                teamData.players.push(player);
                teamData.roundTotalScore += player.roundScore;
            }
        }

        // Sort players within each team by round score descending
        Array.from(teamsMap.values()).forEach((data) => {
            data.players.sort((a: PlayerResultData, b: PlayerResultData) => b.roundScore - a.roundScore);
        });

        // Convert to array sorted by team number, pad to 4 slots
        const sortedTeams = Array.from(teamsMap.values())
            .filter(data => data.players.length > 0)
            .sort((a, b) => a.team.teamNumber - b.team.teamNumber);

        // Always return 4 slots (null for empty)
        const result: (TeamData | null)[] = [null, null, null, null];
        sortedTeams.forEach((team, index) => {
            result[index] = team;
        });

        return result;
    }, [roundResult, gameState, allMaps, locale]);

    // Calculate best team score
    const bestScore = useMemo(() => {
        return Math.max(...teamData.filter(d => d).map(d => d!.roundTotalScore / d!.players.length));
    }, [teamData]);

    // Initialize animated HP values and trigger attack animation
    useEffect(() => {
        if (!gameState || hasAnimatedRef.current) return;
        hasAnimatedRef.current = true;

        // Find the winning team (highest round score) and initialize HP
        let winningTeamIndex = -1;
        let highestScore = -1;

        teamData.forEach((data, index) => {
            if (data) {
                // Use the snapshot's previousHp for accurate animation (fallback to calculation if no snapshot)
                const previousHp = data.snapshot?.previousHp ?? (data.team.currentHp + data.team.lastRoundDamage);
                setAnimatedHp(prev => ({ ...prev, [data.team.id]: previousHp }));

                if (data.roundTotalScore > highestScore) {
                    highestScore = data.roundTotalScore;
                    winningTeamIndex = index;
                }
            }
        });

        // After delay, trigger damage animation on teams that took damage
        const attackDelay = setTimeout(() => {
            teamData.forEach((data, index) => {
                const damage = data?.snapshot?.lastRoundDamage ?? data?.team.lastRoundDamage ?? 0;
                if (data && index !== winningTeamIndex && damage > 0) {
                    triggerDamageAnimation(data.team.id);
                }
            });
        }, ANIMATION_TIMING.ATTACK_DELAY * 1000);

        return () => clearTimeout(attackDelay);
    }, [roundResult.roundNumber]);

    // Trigger damage animation on a team
    const triggerDamageAnimation = (teamId: string) => {
        const data = teamData.find(d => d?.team.id === teamId);
        if (!data) return;

        // Use snapshot values for accurate animation
        const previousHp = data.snapshot?.previousHp ?? (data.team.currentHp + data.team.lastRoundDamage);
        const targetHp = data.snapshot?.currentHp ?? data.team.currentHp;

        setHitTeams(prev => new Set(prev).add(teamId));

        // Animate HP decrease
        animate(previousHp, targetHp, {
            duration: ANIMATION_TIMING.HP_DECREASE_DURATION,
            ease: "easeOut",
            onUpdate: (value) => {
                setAnimatedHp(prev => ({ ...prev, [teamId]: Math.round(value) }));
            },
            onComplete: () => {
                setTimeout(() => {
                    setHitTeams(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(teamId);
                        return newSet;
                    });
                }, ANIMATION_TIMING.HIT_FLASH_LINGER * 1000);
            }
        });
    };

    if (!gameState || !room) return null;

    return (
        <div className="relative px-4 md:px-0">
            <div className="absolute w-full translate-x-[-25px] h-full flex z-[1] blur-sm">
                <div className="w-1/2 h-full bg-gradient-to-l from-black/50 via-black/30 via-[88%] to-transparent"></div>
                <div className="w-1/2 h-full bg-gradient-to-r from-black/50 via-black/30 via-[88%] to-transparent"></div>
            </div>
            <div className="relative w-full flex flex-col gap-1 2xl:gap-4 text-white z-[2] mt-1 2xl:mt-4">
                {isMobile ? (
                    // Mobile: single column layout
                    <div className="flex flex-col gap-2 w-full">
                        {teamData.filter(data => data !== null).map((data, index) => (
                            <div key={index} className="w-full">
                                <TeamBlock
                                    teamData={data!}
                                    onHoverMapPlayer={onHoverMapPlayer}
                                    onClickMapPlayer={onClickMapPlayer}
                                    lockedMapPlayer={lockedMapPlayer}
                                    align="left"
                                    animatedHp={animatedHp[data!.team.id]}
                                    isHit={hitTeams.has(data!.team.id)}
                                    bestScore={bestScore}
                                    isMobile={true}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    // Desktop: 2x2 grid layout
                    [0, 2].map(rowStart => (
                        <div key={rowStart} className="flex gap-1 2xl:gap-4 w-full">
                            {teamData.slice(rowStart, rowStart + 2).map((data, index) => (
                                <div
                                    key={rowStart + index}
                                    className="basis-1/2 shrink-0"
                                >
                                    {data ? (
                                        <TeamBlock
                                            teamData={data}
                                            onHoverMapPlayer={onHoverMapPlayer}
                                            onClickMapPlayer={onClickMapPlayer}
                                            lockedMapPlayer={lockedMapPlayer}
                                            align={index % 2 === 0 ? 'left' : 'right'}
                                            animatedHp={animatedHp[data.team.id]}
                                            isHit={hitTeams.has(data.team.id)}
                                            bestScore={bestScore}
                                            isMobile={false}
                                        />
                                    ) : (
                                        <div className="h-full w-full opacity-20 flex items-center justify-center">
                                            {/* Empty team slot */}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

interface TeamBlockProps {
    teamData: TeamData;
    onHoverMapPlayer?: (playerId: string | null) => void;
    onClickMapPlayer?: (playerId: string | null) => void;
    lockedMapPlayer?: string | null;
    align?: 'left' | 'right';
    animatedHp?: number;
    isHit?: boolean;
    bestScore: number;
    isMobile?: boolean;
}

function TeamBlock({
    teamData,
    onHoverMapPlayer,
    onClickMapPlayer,
    lockedMapPlayer,
    align = 'left',
    animatedHp,
    isHit = false,
    bestScore,
    isMobile = false,
}: TeamBlockProps) {
    const { gameState } = useGame();
    const { team, players, roundTotalScore, snapshot } = teamData;
    const { t } = useTranslation();
    const { play } = useAudio();

    const maxHp = snapshot?.maxHp ?? gameState?.gameSettings.teamMaxHp ?? 10000;
    const displayHp = animatedHp ?? snapshot?.currentHp ?? team.currentHp;
    const hpPercent = (displayHp / maxHp) * 100;
    const lastRoundDamage = snapshot?.lastRoundDamage ?? team.lastRoundDamage;
    const isEliminated = snapshot?.isEliminated ?? team.isEliminated;
    const wasEliminatedThisRound = snapshot?.wasEliminatedThisRound ?? false;
    // Show eliminated effect only if team was already eliminated before this round
    const showEliminatedEffect = isEliminated && !wasEliminatedThisRound && lastRoundDamage === 0;

    // Calculate duplicate numbers for avatars within this team
    const duplicateNumbers = useMemo(() => {
        return calculateDuplicateNumbers(players.map(p => ({ sessionId: p.sessionId, avatarId: p.avatarId })));
    }, [players]);

    // Shadow logic for scrollable area
    const [showTopShadow, setShowTopShadow] = React.useState(false);
    const [showBottomShadow, setShowBottomShadow] = React.useState(false);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const handleScroll = () => {
            setShowTopShadow(el.scrollTop > 0 && players.length > 4);
            setShowBottomShadow(el.scrollTop + el.clientHeight < el.scrollHeight && players.length > 4);
        };
        handleScroll(); // Initial
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [players.length]);

    return (
        <motion.div
            className="text-white w-full h-full relative flex flex-col md:mx-3"
            animate={isHit ? {
                x: [0, -5, 5, -3, 3, 0],
                filter: ['brightness(1)', 'brightness(1.5)', 'brightness(1)'],
            } : {}}
            transition={{ duration: ANIMATION_TIMING.HIT_EFFECT_DURATION }}
        >
            {/* Hit flash overlay */}
            <AnimatePresence>
                {isHit && (
                    <motion.div
                        className="absolute inset-0 z-30 bg-red-500/30 rounded pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 0.5, 0] }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: ANIMATION_TIMING.HIT_EFFECT_DURATION }}
                    />
                )}
            </AnimatePresence>
            {/* Background blur */}
            {!isMobile && (
                <div className="absolute z-10 top-0 w-full h-full left-0 blur-sm flex opacity-30">
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to right, transparent 0%, rgb(0,0,0) 50%, rgb(0,0,0) 100%)`
                        }}
                    ></div>
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to left, transparent 0%, transparent 0%, rgb(0,0,0) 50%, rgb(0,0,0) 100%)`
                        }}
                    ></div>
                </div>
            )}

            {/* Team Header */}
            <div className={`z-20 relative text-[20px] 2xl:text-[28px] h-3 2xl:h-5 font-semibold font-orgon-plan uppercase whitespace-nowrap flex gap-2 items-center ${align === 'left' ? 'justify-start' : 'flex-row-reverse'} ${showEliminatedEffect ? 'opacity-50' : ''}`}>
                <div className="absolute z-10 top-0 w-full h-full left-0 blur-sm flex opacity-40">
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to right, transparent 0%, ${getTeamColorHex(team.color)}b3 ${align === 'left' ? '20%' : '50%'}, ${getTeamColorHex(team.color)}b3 100%)`
                        }}
                    ></div>
                    <div
                        className="px-0 2xl:px-10 w-1/2 h-full"
                        style={{
                            background: `linear-gradient(to left, transparent 0%, ${getTeamColorHex(team.color)}b3 ${align === 'right' ? '20%' : '50%'}, ${getTeamColorHex(team.color)}b3 100%)`
                        }}
                    ></div>
                </div>
                <div
                    className="relative z-10 w-[14px] h-[14px] 2xl:w-[18px] 2xl:h-[18px] rounded border border-yellow-300 shadow-[inset_0_0_6px_#000]"
                    style={{ backgroundColor: getTeamColorHex(team.color) }}
                />
                <span
                    className={`relative z-10 text-shadow shadow-yellow-800`}
                    style={{ transform: 'rotateX(60deg)' }}
                >
                    {team.name}
                </span>
            </div>

            {/* Content */}
            <div className={`z-20 relative w-full mt-1 grow flex transition-all duration-500 ${showEliminatedEffect ? 'grayscale opacity-50' : ''}`}>
                <div className="flex-1 flex flex-col justify-between h-full">
                    <div className="relative grow">
                        {/* Top shadow */}
                        <div
                            className="pointer-events-none absolute top-0 left-0 w-full h-8 z-20 bg-gradient-to-b from-black/40 to-transparent duration-300"
                            style={showTopShadow ? {} : { opacity: 0 }}
                        />
                        {/* Bottom shadow */}
                        <div
                            className="pointer-events-none absolute bottom-0 left-0 w-full h-8 z-20 bg-gradient-to-t from-black/40 to-transparent duration-300"
                            style={showBottomShadow ? {} : { opacity: 0 }}
                        />

                        <div
                            ref={scrollRef}
                            className={`flex gap-2 scrollbar-hide max-h-none md:max-h-[50px] 2xl:max-h-[125px] ${players.length > 4 ? '2xl:overflow-y-auto' : '2xl:overflow-hidden'} ${players.length > 2 ? 'overflow-y-auto' : 'overflow-hidden'} ${align === 'left' ? '' : 'flex-row-reverse'}`}
                        >
                            {/* Players Column */}
                            <div className="flex flex-col gap-0.5 2xl:gap-1 grow h-fit w-[100px] md:w-[150px] max-w-[150px] 2xl:w-[230px] 2xl:max-w-none">
                                {players.map((player) => (
                                    <div
                                        key={player.sessionId}
                                        className={`h-[20] 2xl:h-[25px] flex gap-2 items-center ${player.isEliminated ? 'grayscale opacity-50' : ''} ${align === 'left' ? '' : 'flex-row-reverse'}`}
                                    >
                                        <div className="w-[20px] h-[20px] 2xl:w-[25px] 2xl:h-[25px] relative">
                                            <PlayerAvatar
                                                avatarId={player.avatarId}
                                                duplicateNumber={duplicateNumbers.get(player.sessionId) || 0}
                                                size={isMobile ? 20 : 25}
                                                className="w-full h-full"
                                                isLeader={gameState?.gameMasterId === player.sessionId}
                                                leaderIconSize={16}
                                            />
                                        </div>
                                        <div className={`flex items-center gap-2 ${align === 'left' ? '' : 'flex-row-reverse'}`}>
                                            <div className="text-shadow shadow-blue-800 font-myriad text-sm 2xl:text-lg whitespace-nowrap max-w-[80px] md:max-w-[90px] 2xl:max-w-none truncate">{player.name}</div>
                                            <div className="w-5 h-5 2xl:w-6 2xl:h-6 flex items-center justify-center">
                                                {gameState?.players?.get(player.sessionId)?.isReady && (
                                                    <Image
                                                        src='/checkmark.png'
                                                        alt="Check Icon"
                                                        height={24}
                                                        width={24}
                                                        className="translate-y-[0px]"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Guesses Column */}
                            <div className="flex flex-col gap-0.5 2xl:gap-1 grow h-fit w-[100px] md:w-[150px] 2xl:w-[230px] max-w-[130px] md:max-w-[150px] 2xl:max-w-none">
                                {players.map((player) => (
                                    <div
                                        key={player.sessionId}
                                        className={`text-shadow shadow-blue-600 font-myriad text-sm 2xl:text-lg whitespace-nowrap flex items-center h-[20] 2xl:h-[25px] relative ${player.isEliminated && !player.hasGuessed ? 'opacity-0 pointer-events-none' : ''} ${player.isEliminated && player.hasGuessed ? 'grayscale opacity-50' : ''} ${align === 'left' ? '' : 'flex-row-reverse'}`}
                                    >
                                        {player.hasGuessed && player.guessedOnCorrectMap && (
                                            player.roundScore === 1000 ? (
                                                t("play.onTheSpot")
                                            ) : (
                                                <div className="flex gap-3 items-center relative">
                                                    {t("play.yalmsAway", { distance: convertDistanceToYalms(player.distance ?? 0) })}
                                                    <TooltipWithIconFFXIV
                                                        text={t("play.accuracyTooltip")}
                                                        size="small"
                                                        className="text-sm 2xl:text-lg"
                                                    />
                                                </div>
                                            )
                                        )}
                                        {!player.hasGuessed && t("play.noGuess")}
                                        {player.hasGuessed && !player.guessedOnCorrectMap && (
                                            <div className="flex gap-2 items-center">
                                                <span>{player.guessedMapName}</span>
                                                {onHoverMapPlayer && onClickMapPlayer && (
                                                    <div
                                                        onMouseEnter={() => {
                                                            play(AUDIO.HOVER);
                                                            if (!lockedMapPlayer) {
                                                                onHoverMapPlayer(player.sessionId);
                                                            }
                                                        }}
                                                        onMouseLeave={() => onHoverMapPlayer(null)}
                                                        onClick={() => {
                                                            play(AUDIO.CLICK);
                                                            if (lockedMapPlayer === player.sessionId) {
                                                                onClickMapPlayer(null);
                                                            } else {
                                                                onClickMapPlayer(player.sessionId);
                                                            }
                                                        }}
                                                        className={`cursor-pointer flex items-center transition-all w-[20px] h-[20px] 2xl:w-[25px] 2xl:h-[25px] duration-200 ${lockedMapPlayer === player.sessionId
                                                            ? 'scale-110 drop-shadow-[0_0_8px_rgba(37,99,235,0.8)]'
                                                            : 'hover:scale-105'
                                                            }`}
                                                    >
                                                        <Image
                                                            src="/map_icon.png"
                                                            alt="View Map"
                                                            width={35}
                                                            height={35}
                                                            className={`${lockedMapPlayer === player.sessionId
                                                                ? 'brightness-125 saturate-150'
                                                                : ''
                                                                }`}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {/* Scores */}
                            <div className="flex flex-col gap-0.5 2xl:gap-1 h-fit w-[40px] md:w-[60px] 2xl:w-[88px]">
                                {players.map((player) => (
                                    <div
                                        key={player.sessionId}
                                        className={`h-[20px] 2xl:h-[25px] text-shadow shadow-blue-800 font-contrax text-xs md:text-sm 2xl:text-md whitespace-nowrap flex items-center ${align === 'left' ? '' : 'justify-end'}`}
                                    >
                                        {player.roundScore}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* HP Bar and total score - hide HP bar on mobile */}
                    <div className={`relative flex md:justify-between items-center gap-2 mt-1 2xl:mt-4 ${align === 'left' ? '' : 'flex-row-reverse'}`}>
                        <div className="relative">
                            {lastRoundDamage > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10, y: "-100%" }}
                                    animate={{ opacity: 1, x: 0, y: "-100%" }}
                                    transition={{ duration: 0.2, delay: ANIMATION_TIMING.ATTACK_DELAY + ANIMATION_TIMING.HP_DECREASE_DURATION + 0.3 }}
                                    className={`hidden 2xl:flex z-[20] absolute -top-0 items-center gap-1 text-red-600 text-shadow-border shadow-slate-900 text-xs font-contrax ${align === 'left' ? 'right-0' : 'left-0'}`}
                                >
                                    <div className="">
                                        - {lastRoundDamage} HP
                                    </div>
                                    <div className="translate-y-[-2px]">
                                        <TooltipWithIconFFXIV
                                            size="small"
                                            className="!font-myriad text-sm 2xl:text-lg"
                                        >
                                            {(() => {
                                                const yourScore = roundTotalScore / players.length;
                                                const difference = bestScore - yourScore;
                                                const multiplier = gameState!.currentDamageMultiplier.toString();
                                                const totalDamage = lastRoundDamage;
                                                const tooltipText = totalDamage === 100 ? t("play.fight.minimumDamageTooltip", {
                                                    multiplier,
                                                    totalDamage
                                                }) : t("play.fight.damageTooltip", {
                                                    bestScore: Math.round(bestScore),
                                                    yourScore: Math.round(yourScore),
                                                    difference: Math.round(difference),
                                                    multiplier,
                                                    totalDamage
                                                });
                                                return <div dangerouslySetInnerHTML={{ __html: tooltipText }} />;
                                            })()}
                                        </TooltipWithIconFFXIV>
                                    </div>
                                </motion.div>
                            )}
                            <BarFFXIV
                                width="w-[287px] md:w-[300px] 2xl:w-[500px]"
                                height="h-2 2xl:h-3.5"
                                percent={hpPercent}
                                number={displayHp}
                                numberSize="text-xs 2xl:text-xl"
                                numberAlign={align === 'left' ? 'right' : 'left'}
                                startFrom={align === 'left' ? 'left' : 'right'}
                                numberText={Math.round(displayHp) + " HP"}
                            />
                        </div>
                        <div className={`flex items-center w-[40px] md:w-[60px] 2xl:w-[88px] ${align === 'left' ? '' : 'flex-row-reverse'}`}>
                            <span className="font-contrax text-sm 2xl:text-xl text-white text-shadow shadow-blue-800">
                                {Math.round(roundTotalScore / players.length)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}